import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { EditPeriodisationFormType, PeriodisationEditorProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { ToggleProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import { Divider, Grid } from "@mui/material";
import { HorizontalFormField, Toggle } from "components/common/wintForms";
import { Periodisation } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const PeriodisationEditor: React.FC<PeriodisationEditorProps> = ({
  values,
  connect,
  setValues,
  lockedInvoiceItemIds,
}) => {
  const { rows, isPeriodisationAdvancedConfiguration, isPeriodisationByDate } = values;
  const isLocked = (invoiceItemId: number) => lockedInvoiceItemIds.has(invoiceItemId);
  const allLocked = rows.length > 0 && rows.every((row) => isLocked(row.invoiceItemId));

  const setPeriodisationForAllRows = (periodisation: InvoiceFormTypes.InvoicePeriodisation) => {
    setValues((prev) => ({
      ...prev,
      isPeriodisationByDate: periodisation.mode === "date",
      rows: prev.rows.map((row) =>
        isLocked(row.invoiceItemId) ? row : { ...row, periodisation: { ...periodisation } }
      ),
    }));
  };

  const onAdvancedConfigurationToggle = (value: boolean) => {
    setValues((prev) => {
      if (value) return { ...prev, isPeriodisationAdvancedConfiguration: true };
      const masterRow = prev.rows.find((row) => !isLocked(row.invoiceItemId));
      const master = masterRow?.periodisation ?? { mode: "month" as PeriodisationMode };
      return {
        ...prev,
        isPeriodisationAdvancedConfiguration: false,
        isPeriodisationByDate: master.mode === "date",
        rows: prev.rows.map((row) => (isLocked(row.invoiceItemId) ? row : { ...row, periodisation: { ...master } })),
      };
    });
  };

  const onByDateToggle = (value: boolean) => {
    const mode: PeriodisationMode = value ? "date" : "month";
    setValues((prev) => ({
      ...prev,
      isPeriodisationByDate: value,
      rows: prev.rows.map((row) =>
        isLocked(row.invoiceItemId) ? row : { ...row, periodisation: { ...row.periodisation, mode, range: undefined } }
      ),
    }));
  };

  const allArticlesLabel = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AllArticles);

  const firstUnlockedIndex = rows.findIndex((row) => !isLocked(row.invoiceItemId));
  const masterRowIndex = firstUnlockedIndex === -1 ? 0 : firstUnlockedIndex;

  const visibleRows = isPeriodisationAdvancedConfiguration
    ? rows.map((row, index) => ({ key: row.invoiceItemId, label: row.text, row, index }))
    : rows[masterRowIndex]
      ? [{ key: rows[masterRowIndex].invoiceItemId, label: allArticlesLabel, row: rows[masterRowIndex], index: masterRowIndex }]
      : [];

  const renderRowField = (row: EditPeriodisationFormType["rows"][number], index: number) => {
    if (isPeriodisationAdvancedConfiguration) {
      return (
        <Periodisation
          {...connect(`rows[${index}].periodisation` as keyof EditPeriodisationFormType)}
          disableModeToggle
          disabled={isLocked(row.invoiceItemId)}
        />
      );
    }
    return (
      <Periodisation
        value={row.periodisation ?? { mode: isPeriodisationByDate ? "date" : "month" }}
        setValue={setPeriodisationForAllRows}
        name={`rows[${index}].periodisation`}
        onChange={() => {}}
        onBlur={() => {}}
        setTouched={() => {}}
        touched={false}
        hasError={false}
        disabled={allLocked}
      />
    );
  };

  return (
    <Grid container direction="column" spacing={1} className={cssExports.editPeriodisationEditor}>
      {visibleRows.map((entry, idx) => (
        <React.Fragment key={entry.key}>
          {idx > 0 && (
            <Grid item>
              <Divider />
            </Grid>
          )}
          <Grid item container alignItems="center" spacing={2} wrap="nowrap">
            <Grid item xs={4} className={cssExports.editPeriodisationRowLabel}>
              {entry.label}
            </Grid>
            <Grid item xs>
              {renderRowField(entry.row, entry.index)}
            </Grid>
          </Grid>
        </React.Fragment>
      ))}

      <Grid item>
        <Divider />
      </Grid>

      <Grid item container direction="column">
        <HorizontalFormField<ToggleProps>
          Component={Toggle}
          name="isPeriodisationAdvancedConfiguration"
          value={isPeriodisationAdvancedConfiguration}
          setValue={onAdvancedConfigurationToggle}
          onChange={() => {}}
          onBlur={() => {}}
          touched
          setTouched={() => {}}
          hasError={false}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EditInRRows)}
        />
        {isPeriodisationAdvancedConfiguration && (
          <HorizontalFormField<ToggleProps>
            Component={Toggle}
            name="isPeriodisationByDate"
            value={isPeriodisationByDate}
            setValue={onByDateToggle}
            onChange={() => {}}
            onBlur={() => {}}
            touched
            setTouched={() => {}}
            hasError={false}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PeriodiseOnDate)}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default PeriodisationEditor;
